import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { getUser, logout } from '../utils/auth';
import { asList, budgetLineAmount, companyName, costCodeLabel, costTypeLabel, getJson, loadCostLookups } from './helpers';
import PortfolioPage from './pages/PortfolioPage';
import ProjectHomePage from './pages/ProjectHomePage';
import ReportsPage from './pages/ReportsPage';
import ToolListPage from './pages/ToolListPage';
import {
  ApInvoiceDetail,
  BudgetDetail,
  ChangeOrderDetail,
  CommitmentChangeOrderDetail,
  CommitmentDetail,
  DirectCostDetail,
  DirectoryDetail,
  OwnerBillingDetail,
} from './pages/RecordDetailPage';
import './procorePreview.css';

function Icon({ d }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d={d} />
    </svg>
  );
}

function ProjectPicker({ projects, current }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="pc-picker">
      <button type="button" className="pc-picker-btn" onClick={() => setOpen((value) => !value)}>
        <span>{current ? `${current.project_number} — ${current.project_name}` : 'Select a project'}</span>
        <span>▾</span>
      </button>
      {open && (
        <div className="pc-picker-menu">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/procore');
            }}
          >
            Company Portfolio
            <span className="pc-meta">All projects</span>
          </button>
          {projects.map((project) => (
            <button
              key={project.vuid}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(`/procore/projects/${project.vuid}`);
              }}
            >
              {project.project_name}
              <span className="pc-meta">{project.project_number}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewBanner() {
  const [hidden, setHidden] = useState(() => localStorage.getItem('pc-preview-banner') === 'hidden');
  if (hidden) return null;
  return (
    <div className="pc-banner">
      <span>
        Preview only — Claret Financials in a Procore-native layout. Your existing Claret pages are unchanged.{' '}
        <Link to="/">Back to Claret</Link>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          localStorage.setItem('pc-preview-banner', 'hidden');
          setHidden(true);
        }}
      >
        ×
      </button>
    </div>
  );
}

function PreviewShell({ children }) {
  const params = useParams();
  const projectVuid = params.projectVuid;
  const user = getUser();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getJson('/api/projects')
      .then((data) => setProjects(asList(data)))
      .catch(() => setProjects([]));
  }, []);
  const current = useMemo(
    () => projects.find((project) => project.vuid === projectVuid) || null,
    [projects, projectVuid]
  );
  const inProject = Boolean(projectVuid);

  const tools = inProject
    ? [
        { to: `/procore/projects/${projectVuid}`, label: 'Home', end: true },
        { to: `/procore/projects/${projectVuid}/budget`, label: 'Budget' },
        { to: `/procore/projects/${projectVuid}/commitments`, label: 'Commitments' },
        { to: `/procore/projects/${projectVuid}/change-orders`, label: 'Change Orders' },
        { to: `/procore/projects/${projectVuid}/invoicing`, label: 'Invoicing' },
        { to: `/procore/projects/${projectVuid}/direct-costs`, label: 'Direct Costs' },
        { to: `/procore/projects/${projectVuid}/reports`, label: 'Reports' },
      ]
    : [
        { to: '/procore', label: 'Portfolio', end: true },
        { to: '/procore/directory', label: 'Directory' },
        { to: '/procore/reports', label: 'Reports' },
      ];

  return (
    <div className="pc-root">
      <nav className="pc-rail" aria-label="Company tools">
        <Link className="pc-mark" to="/procore" title="Claret">
          C
        </Link>
        <NavLink to="/procore" end title="Portfolio" className={({ isActive }) => (isActive && !inProject ? 'pc-active' : '')}>
          <Icon d="M4 10l8-6 8 6v9a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1v-9z" />
        </NavLink>
        <NavLink to={inProject ? `/procore/projects/${projectVuid}` : '/procore'} title="Project">
          <Icon d="M4 7h16v12H4z M8 7V5h8v2" />
        </NavLink>
        <div className="pc-rail-spacer" />
        <NavLink to="/" title="Exit to Claret">
          <Icon d="M15 8l4 4-4 4M19 12H9M9 5H6a1 1 0 00-1 1v12a1 1 0 001 1h3" />
        </NavLink>
      </nav>
      <div className="pc-main">
        <header className="pc-top">
          <div className="pc-brand">
            <strong>{companyName(user)}</strong>
            <span>Claret Financials · Procore-style preview</span>
          </div>
          <ProjectPicker projects={projects} current={current} />
          <div className="pc-search">
            <input
              aria-label="Search"
              placeholder={inProject ? 'Search this project' : 'Search portfolio'}
              defaultValue=""
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !inProject) {
                  window.dispatchEvent(new CustomEvent('pc-search', { detail: event.target.value }));
                }
              }}
            />
          </div>
          <div className="pc-top-actions">
            <span>
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.username || 'User'}
            </span>
            <Link className="pc-exit" to="/">
              Exit preview
            </Link>
            <button type="button" className="pc-exit" onClick={logout}>
              Logout
            </button>
          </div>
        </header>
        <nav className="pc-tools">
          {tools.map((tool) => (
            <NavLink
              key={tool.to}
              to={tool.to}
              end={tool.end}
              className={({ isActive }) => (isActive ? 'pc-active' : '')}
            >
              {tool.label}
            </NavLink>
          ))}
        </nav>
        <PreviewBanner />
        {children}
      </div>
    </div>
  );
}

export default function ProcorePreviewApp() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PreviewShell>
            <PortfolioPage />
          </PreviewShell>
        }
      />
      <Route
        path="directory"
        element={
          <PreviewShell>
            <ToolListPage
              title="Directory"
              crumb="Company"
              description="Vendors and employees as they would appear in a Procore Directory tool."
              fetcher={() => Promise.all([getJson('/api/vendors'), getJson('/api/employees')]).then(([vendors, employees]) => {
                const vendorRows = asList(vendors).map((row) => ({
                  id: row.vuid,
                  href: `/procore/directory/vendors/${row.vuid}`,
                  col1: row.vendor_name || row.company_name,
                  col2: row.vendor_number,
                  col3: 'Vendor',
                  col4: row.status,
                }));
                const employeeRows = asList(employees).map((row) => ({
                  id: row.vuid,
                  href: `/procore/directory/employees/${row.vuid}`,
                  col1: row.employee_name || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
                  col2: row.employee_number,
                  col3: 'Employee',
                  col4: row.status,
                }));
                return [...vendorRows, ...employeeRows];
              })}
              columns={[
                { key: 'col1', label: 'Name' },
                { key: 'col2', label: 'Number' },
                { key: 'col3', label: 'Type' },
                { key: 'col4', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="reports"
        element={
          <PreviewShell>
            <ReportsPage />
          </PreviewShell>
        }
      />
      <Route
        path="reports/:reportId"
        element={
          <PreviewShell>
            <ReportsPage />
          </PreviewShell>
        }
      />
      <Route
        path="directory/:kind/:recordVuid"
        element={
          <PreviewShell>
            <DirectoryDetail />
          </PreviewShell>
        }
      />
      <Route path="projects/:projectVuid/*" element={<ProjectRoutes />} />
    </Routes>
  );
}

function ProjectRoutes() {
  const { projectVuid } = useParams();
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PreviewShell>
            <ProjectHomePage projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
      <Route
        path="commitments/:recordVuid"
        element={
          <PreviewShell>
            <CommitmentDetail />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing/ap/:recordVuid"
        element={
          <PreviewShell>
            <ApInvoiceDetail />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing/ar/:recordVuid"
        element={
          <PreviewShell>
            <OwnerBillingDetail />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders/commitment/:recordVuid/:coNumber"
        element={
          <PreviewShell>
            <CommitmentChangeOrderDetail />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders/:kind/:recordVuid"
        element={
          <PreviewShell>
            <ChangeOrderDetail />
          </PreviewShell>
        }
      />
      <Route
        path="direct-costs/:recordVuid"
        element={
          <PreviewShell>
            <DirectCostDetail />
          </PreviewShell>
        }
      />
      <Route
        path="budget/:recordVuid"
        element={
          <PreviewShell>
            <BudgetDetail />
          </PreviewShell>
        }
      />
      <Route
        path="budget"
        element={
          <PreviewShell>
            <ToolListPage
              title="Budget"
              projectVuid={projectVuid}
              fetcher={async () => {
                const [{ codeMap, typeMap }, budgetPayload] = await Promise.all([
                  loadCostLookups(projectVuid),
                  getJson('/api/project-budgets', { project_vuid: projectVuid }),
                ]);
                const budgets = asList(budgetPayload).filter((row) => row.project_vuid === projectVuid);
                return budgets.flatMap((budget) => {
                  const lines = budget.budget_lines || budget.lines || [];
                  if (!lines.length) {
                    return [{
                      id: budget.vuid,
                      href: `/procore/projects/${projectVuid}/budget/${budget.vuid}`,
                      col1: budget.description || budget.budget_type || 'Budget',
                      col2: '—',
                      col3: budget.budget_amount,
                    }];
                  }
                  return lines.map((line) => ({
                    id: line.vuid,
                    href: `/procore/projects/${projectVuid}/budget/${budget.vuid}`,
                    col1: costCodeLabel(line, codeMap),
                    col2: costTypeLabel(line, typeMap),
                    col3: budgetLineAmount(line),
                  }));
                });
              }}
              columns={[
                { key: 'col1', label: 'Cost Code' },
                { key: 'col2', label: 'Cost Type' },
                { key: 'col3', label: 'Original Budget', money: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="commitments"
        element={
          <PreviewShell>
            <ToolListPage
              title="Commitments"
              projectVuid={projectVuid}
              description="Work order contracts pushed from Procore or retrieved into Claret — origin_id is written back on success."
              fetcher={async () => {
                const rows = asList(await getJson('/api/project-commitments')).filter(
                  (row) => row.project_vuid === projectVuid
                );
                return rows.map((row) => ({
                  id: row.vuid,
                  href: `/procore/projects/${projectVuid}/commitments/${row.vuid}`,
                  col1: row.commitment_number,
                  col2: row.commitment_name,
                  col3: row.vendor_name,
                  col4: row.original_amount ?? row.commitment_amount,
                  col5: row.status,
                }));
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Title' },
                { key: 'col3', label: 'Vendor' },
                { key: 'col4', label: 'Original Amount', money: true },
                { key: 'col5', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="change-orders"
        element={
          <PreviewShell>
            <ToolListPage
              title="Change Orders"
              projectVuid={projectVuid}
              description="Prime and commitment change orders, including records posted from Procore workflows."
              fetcher={async () => {
                const [external, internal, commitments] = await Promise.all([
                  getJson('/api/external-change-orders').catch(() => []),
                  getJson('/api/internal-change-orders').catch(() => []),
                  getJson('/api/project-commitments').catch(() => []),
                ]);
                const extRows = asList(external)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/change-orders/external/${row.vuid}`,
                    col1: row.change_order_number,
                    col2: row.description || row.change_order_name,
                    col3: 'Prime / External',
                    col4: row.total_amount,
                    col5: row.status,
                  }));
                const intRows = asList(internal)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/change-orders/internal/${row.vuid}`,
                    col1: row.change_order_number,
                    col2: row.description || row.change_order_name,
                    col3: 'Internal',
                    col4: row.total_amount,
                    col5: row.status,
                  }));
                const projectCommitments = asList(commitments).filter((row) => row.project_vuid === projectVuid);
                const commitmentDetails = await Promise.all(
                  projectCommitments.map((row) => getJson(`/api/commitments/${row.vuid}`).catch(() => row))
                );
                const ccoRows = [];
                commitmentDetails.forEach((commitment) => {
                  const grouped = {};
                  (commitment.line_items || []).forEach((line) => {
                    const number = String(line.change_order_number || '').trim();
                    if (!number) return;
                    if (!grouped[number]) grouped[number] = 0;
                    grouped[number] += Number(line.total_cost || 0);
                  });
                  Object.entries(grouped).forEach(([number, amount]) => {
                    ccoRows.push({
                      id: `${commitment.vuid}-${number}`,
                      href: `/procore/projects/${projectVuid}/change-orders/commitment/${commitment.vuid}/${encodeURIComponent(number)}`,
                      col1: number,
                      col2: commitment.commitment_name || commitment.commitment_number,
                      col3: 'Commitment',
                      col4: amount,
                      col5: commitment.status,
                    });
                  });
                });
                return [...extRows, ...intRows, ...ccoRows];
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Title' },
                { key: 'col3', label: 'Type' },
                { key: 'col4', label: 'Amount', money: true },
                { key: 'col5', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="invoicing"
        element={
          <PreviewShell>
            <ToolListPage
              title="Invoicing"
              projectVuid={projectVuid}
              description="Subcontractor invoices and owner billings — Claret posts the accounting that Procore does not."
              fetcher={async () => {
                const [ap, billings] = await Promise.all([
                  getJson('/api/ap-invoices').catch(() => []),
                  getJson('/api/project-billings').catch(() => []),
                ]);
                const apRows = asList(ap)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/invoicing/ap/${row.vuid}`,
                    col1: row.invoice_number,
                    col2: row.vendor_name || 'AP Invoice',
                    col3: 'Subcontractor',
                    col4: row.total_amount,
                    col5: row.status,
                  }));
                const arRows = asList(billings)
                  .filter((row) => row.project_vuid === projectVuid)
                  .map((row) => ({
                    id: row.vuid,
                    href: `/procore/projects/${projectVuid}/invoicing/ar/${row.vuid}`,
                    col1: row.billing_number || row.application_number,
                    col2: row.billing_name || 'Owner billing',
                    col3: 'Owner',
                    col4: row.total_amount || row.current_payment_due,
                    col5: row.status,
                  }));
                return [...apRows, ...arRows];
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Title' },
                { key: 'col3', label: 'Type' },
                { key: 'col4', label: 'Amount', money: true },
                { key: 'col5', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="direct-costs"
        element={
          <PreviewShell>
            <ToolListPage
              title="Direct Costs"
              projectVuid={projectVuid}
              fetcher={async () => {
                const rows = asList(await getJson('/api/project-expenses').catch(() => [])).filter(
                  (row) => row.project_vuid === projectVuid
                );
                return rows.map((row) => ({
                  id: row.vuid,
                  href: `/procore/projects/${projectVuid}/direct-costs/${row.vuid}`,
                  col1: row.expense_number || row.invoice_number || '—',
                  col2: row.description || row.vendor_name,
                  col3: row.expense_date || row.date,
                  col4: row.total_amount || row.amount,
                  col5: row.status,
                }));
              }}
              columns={[
                { key: 'col1', label: '#' },
                { key: 'col2', label: 'Description' },
                { key: 'col3', label: 'Date' },
                { key: 'col4', label: 'Amount', money: true },
                { key: 'col5', label: 'Status', status: true },
              ]}
            />
          </PreviewShell>
        }
      />
      <Route
        path="reports"
        element={
          <PreviewShell>
            <ReportsPage projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
      <Route
        path="reports/:reportId"
        element={
          <PreviewShell>
            <ReportsPage projectVuid={projectVuid} />
          </PreviewShell>
        }
      />
    </Routes>
  );
}
